import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import { format } from 'date-fns';

// A very simple chart component with direct data rendering
const SimpleSensorChart = ({ 
  data = [], 
  sensorId = 'm1',
  title = '',
  loading = false,
  threshold = null,
  smooth = true
}) => {
  const theme = useTheme();
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={40} />
        <Typography sx={{ ml: 2 }}>Loading data...</Typography>
      </Box>
    );
  }
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="error">No data available to display</Typography>
      </Box>
    );
  }
  
  // Check if we have any valid data points for this sensor
  const hasValidData = data.some(item => item[sensorId] !== null && item[sensorId] !== undefined && !isNaN(item[sensorId]));
  
  if (!hasValidData) {
    // Show a message when no valid data is available
    return (
      <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
        <Typography variant="h6" gutterBottom>
          {title || `Data Visualization for ${sensorId}`}
        </Typography>
        
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100% - 40px)',
          width: '100%',
          border: '1px dashed #ccc',
          borderRadius: 1
        }}>
          <Typography variant="body1" color="error">
            No valid data points for {sensorId}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Data is available but contains no valid values for this sensor
          </Typography>
        </Box>
      </Box>
    );
  }
  
  // Use direct data props for stability - we don't want state management here
  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {title && <Typography variant="h6" gutterBottom>{title}</Typography>}
      
      {/* Debug info */}
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        right: 0, 
        backgroundColor: 'rgba(255,255,255,0.7)',
        padding: 0.5,
        borderRadius: 1,
        fontSize: '10px',
        zIndex: 1000
      }}>
        <Typography variant="caption" sx={{ display: 'block' }}>
          {data.length} points | {sensorId}: {
            data.filter(d => d[sensorId] !== null && d[sensorId] !== undefined).length
          } valid
        </Typography>
      </Box>
      
      <Box sx={{ width: '100%', height: title ? 'calc(100% - 40px)' : '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 20, right: 30, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={(timestamp) => {
                if (!timestamp) return '';
                try {
                  return format(new Date(timestamp), 'MM/dd');
                } catch (err) {
                  console.error('Error formatting date', err);
                  return '';
                }
              }}
            />
            <YAxis domain={['auto', 'auto']} />
            <Tooltip 
              labelFormatter={(timestamp) => {
                if (!timestamp) return '';
                try {
                  return format(new Date(timestamp), 'MM/dd/yyyy');
                } catch (err) {
                  console.error('Error formatting tooltip label', err);
                  return timestamp;
                }
              }}
            />
            <Legend />
            
            {/* Show threshold if provided */}
            {threshold && (
              <ReferenceLine 
                y={threshold} 
                stroke={theme.palette.error.main || "#f44336"}
                strokeDasharray="3 3" 
                label={{ 
                  value: `Threshold: ${threshold}`, 
                  fill: theme.palette.error.main || "#f44336",
                  fontSize: 12
                }} 
              />
            )}
            
            <Line
              type={smooth ? "catmullRom" : "monotone"}
              strokeWidth={3}
              dataKey={sensorId}
              stroke={sensorId === 'm1' ? "#e91e63" : "#2196f3"}
              activeDot={{ r: 6 }}
              dot={{ r: 3 }}
              name={`${sensorId.toUpperCase()} Reading`}
              connectNulls={true}
              isAnimationActive={false}
              // Add additional smoothing parameters
              {...(smooth ? { catmullRom: true, catmullRomTension: 0.2 } : {})}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default SimpleSensorChart;